// src/services/analytics.ts — analytics events, shipped to the native host over the JS bridge.
//
// The native app owns the Firebase SDK; this service only describes events and hands them over as
// `analytics.event` envelopes. When no native transport is present (plain browser, or a host that
// drops the channel) every event is still written to the console with console.info, so behaviour
// stays verifiable during development.
import { bridge } from '@/services/bridge'
import type { RouteLocationNormalized } from 'vue-router'

const BRIDGE_ACTION = 'analytics.event'
const SCREEN_PREFIX = 'Game On'
const NON_NATIVE_PLATFORMS = ['browser', 'unknown']

const STYLE_EVENT =
    'background: #55c5d9; color: #000; padding: 3px 10px 2px 10px; border-radius:4px;'
const STYLE_ERROR =
    'background: #9f0000; color: #fff; padding: 3px 10px 2px 10px; border-radius:4px;'

export type AnalyticsParams = Record<string, unknown>

export interface AnalyticsEvent {
    name: string
    params: AnalyticsParams
    ts: number
    screen: string | null
    app: { version?: string; commit?: string; platform: string; transport: string }
}

// Route `meta.analytics` block — the single source of truth for screen names. `screen` may be a
// function when the name depends on the query or params of the route (the Setups tabs, for
// instance). `title` is accepted as an alias so either key works in a route definition.
export interface AnalyticsRouteMeta {
    screen?: string | ((to: RouteLocationNormalized) => string)
    title?: string
    params?: AnalyticsParams
    enabled?: boolean
}

// 'match-setup' -> 'Match Setup'
function titleCase(value: string): string {
    return value
        .replace(/^\//, '')
        .split(/[-_/]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
}

// Params that stay attached to every event fired while a screen is current, so in-game events can
// be attributed without each caller repeating them.
//
// `match_id` comes off the `:matchId` route param (see trackPageView), so every event fired
// anywhere under /matches/:matchId/* carries it without the caller passing it along. The match
// already exists by that point: selectMatchType() POSTs it before routing to the setup screen.
const STICKY_SCREEN_PARAMS = ['game_type', 'game_context', 'match_id', 'online']

class AnalyticsService {
    private screen: string | null = null
    private stickyParams: AnalyticsParams = {}
    private enabled = import.meta.env.VITE_ANALYTICS_ENABLED !== 'false'
    private debug = import.meta.env.DEV
    private app = {
        version: import.meta.env.VITE_APP_VERSION,
        commit: import.meta.env.VITE_GIT_COMMIT,
    }

    // Optional: reports which transport analytics will travel over. Nothing is gated on it, so
    // events tracked before init() are still delivered.
    public init(): void {
        console.info('%c Analytics ready ', STYLE_EVENT, {
            platform: bridge.platform,
            transport: bridge.transport,
            native: this.isNative,
            enabled: this.enabled,
            ...this.app,
        })
    }

    public configure(opts: { debug?: boolean; enabled?: boolean } = {}): void {
        if (typeof opts.debug === 'boolean') this.debug = opts.debug
        if (typeof opts.enabled === 'boolean') this.enabled = opts.enabled
    }

    public trackEvent(name: string, params: AnalyticsParams = {}): void {
        this.dispatch({
            name,
            params: { ...this.stickyParams, ...params },
            ts: Date.now(),
            screen: this.screen,
            app: { ...this.app, platform: bridge.platform, transport: bridge.transport },
        })
    }

    // One screen_view per screen entry. Repeating the current screen name is ignored, so re-renders
    // and data refreshes cannot produce duplicates; navigating away and back does fire again.
    public trackScreen(screenName: string, params: AnalyticsParams = {}): void {
        if (!screenName || screenName === this.screen) return

        const previous = this.screen
        this.screen = screenName
        this.stickyParams = Object.fromEntries(
            STICKY_SCREEN_PARAMS.filter((key) => key in params).map((key) => [key, params[key]]),
        )
        this.trackEvent('screen_view', {
            screen_name: screenName,
            previous_screen: previous,
            ...params,
        })
    }

    // Router hook. Screen name comes from `to.meta.analytics`, falling back to the route name.
    public trackPageView(to: RouteLocationNormalized, from?: RouteLocationNormalized): void {
        const meta = (to.meta?.analytics ?? {}) as AnalyticsRouteMeta
        if (meta.enabled === false) return

        const configured = typeof meta.screen === 'function' ? meta.screen(to) : meta.screen
        const screen =
            configured ||
            meta.title ||
            `${SCREEN_PREFIX} - ${titleCase(String(to.name ?? to.path))}`

        this.trackScreen(screen, {
            path: to.fullPath,
            route_name: to.name ?? null,
            ...(to.params?.matchId ? { match_id: String(to.params.matchId) } : {}),
            ...(Object.keys(to.params ?? {}).length ? { route_params: to.params } : {}),
            from: from?.name ?? null,
            ...(to.meta?.layout ? { layout: to.meta.layout } : {}),
            ...meta.params,
        })
    }

    public trackError(error: unknown, params: AnalyticsParams = {}): void {
        const message = error instanceof Error ? error.message : String(error)
        console.info('%c Analytics error ', STYLE_ERROR, message, params)
        this.trackEvent('app_error', { message, ...params })
    }

    public get currentScreen(): string | null {
        return this.screen
    }

    public get isNative(): boolean {
        return !NON_NATIVE_PLATFORMS.includes(bridge.platform)
    }

    private dispatch(event: AnalyticsEvent): void {
        if (!this.enabled) return

        // Without a native host this console line is the only observable output — keep it.
        if (this.debug || !this.isNative) {
            console.info(`%c Analytics ${event.name} `, STYLE_EVENT, event.params)
        }

        try {
            bridge.emit(BRIDGE_ACTION, event)
        } catch (e) {
            // bridge.emit throws UNAVAILABLE / SEND_FAILED. Analytics must never break a caller.
            console.warn('Analytics: bridge send failed', e)
        }
    }
}

export default new AnalyticsService()
